import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Check, X, Loader2, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

interface IntakeForm {
  id: string;
  title: string;
  form_type: string;
  file_url: string | null;
  external_url: string | null;
}

interface ConfirmDeclineActionsProps {
  bookingId: string;
  onSuccess?: () => void;
}

export const ConfirmDeclineActions: React.FC<ConfirmDeclineActionsProps> = ({
  bookingId,
  onSuccess,
}) => {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();
  const { user } = useAuth();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [intakeForms, setIntakeForms] = useState<IntakeForm[]>([]);
  const [selectedFormIds, setSelectedFormIds] = useState<string[]>([]);
  const [loadingForms, setLoadingForms] = useState(false);

  // Fetch intake forms when confirm dialog opens
  useEffect(() => {
    if (confirmDialogOpen && user?.id) {
      setLoadingForms(true);
      supabase
        .from('practitioner_intake_forms' as any)
        .select('id, title, form_type, file_url, external_url')
        .eq('practitioner_id', user.id)
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .then(({ data }) => {
          const forms = (data as any[] || []) as IntakeForm[];
          setIntakeForms(forms);
          // Pre-select all forms by default
          setSelectedFormIds(forms.map(f => f.id));
          setLoadingForms(false);
        });
    }
  }, [confirmDialogOpen, user?.id]);

  const toggleForm = (formId: string) => {
    setSelectedFormIds(prev =>
      prev.includes(formId) ? prev.filter(id => id !== formId) : [...prev, formId]
    );
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      const { error } = await supabase.functions.invoke('confirm-booking', {
        body: { bookingId, selectedFormIds }
      });

      if (error) throw error;

      toast({
        title: t('appointments.actions.confirmSuccess'),
        description: t('appointments.confirmSuccess'),
      });

      setConfirmDialogOpen(false);
      onSuccess?.();
    } catch (error: any) {
      console.error('Error confirming booking:', error);
      toast({
        title: t('error'),
        description: error.message || 'Failed to confirm booking',
        variant: 'destructive',
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      toast({
        title: t('error'),
        description: 'Please provide a reason for declining',
        variant: 'destructive',
      });
      return;
    }

    setIsDeclining(true);
    try {
      const { error } = await supabase.functions.invoke('decline-booking', {
        body: { 
          bookingId,
          reason: declineReason 
        }
      });

      if (error) throw error;

      toast({
        title: t('appointments.actions.declineSuccess'),
        description: t('appointments.declineSuccess'),
      });

      setDeclineReason('');
      onSuccess?.();
    } catch (error: any) {
      console.error('Error declining booking:', error);
      toast({
        title: t('error'),
        description: error.message || 'Failed to decline booking',
        variant: 'destructive',
      });
    } finally {
      setIsDeclining(false);
    }
  };

  return (
    <div className="flex gap-2">
      {/* Confirm with form selection dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogTrigger asChild>
          <Button
            size="sm"
            disabled={isConfirming}
            className="bg-[#C8A25D] hover:bg-[#B38E4A] text-white shadow-md ring-1 ring-[#C8A25D]/40 hover:ring-[#B38E4A]/60"
          >
            <Check className="h-4 w-4 mr-1" />
            Confirm
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Booking</DialogTitle>
            <DialogDescription>
              Select which consent/intake forms to send to the client with the confirmation email.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            {loadingForms ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading forms...</span>
              </div>
            ) : intakeForms.length === 0 ? (
              <div className="text-sm text-muted-foreground bg-muted/50 rounded-md p-3">
                <FileText className="h-4 w-4 inline mr-1.5" />
                No intake forms uploaded yet. You can add forms in your Profile settings.
                The confirmation email will be sent without attachments.
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-foreground">Intake Forms</p>
                {intakeForms.map(form => (
                  <label
                    key={form.id}
                    className="flex items-center gap-3 p-2.5 rounded-md border cursor-pointer hover:bg-accent/50 transition-colors"
                  >
                    <Checkbox
                      checked={selectedFormIds.includes(form.id)}
                      onCheckedChange={() => toggleForm(form.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium truncate block">{form.title}</span>
                      <span className="text-xs text-muted-foreground capitalize">{form.form_type}</span>
                    </div>
                  </label>
                ))}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isConfirming}
              className="bg-[#C8A25D] hover:bg-[#B38E4A] text-white"
            >
              {isConfirming ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Confirming...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Confirm & Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline dialog */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={isDeclining}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <X className="h-4 w-4 mr-1" />
            Decline
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Booking</AlertDialogTitle>
            <AlertDialogDescription>
              {t('appointments.declineDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <label className="text-sm font-medium mb-2 block">
              {t('appointments.declineReason')}
            </label>
            <Textarea
              placeholder={t('appointments.declineReasonPlaceholder')}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDecline}
              disabled={isDeclining || !declineReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeclining ? t('loading') : 'Decline'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
